(ns hai.contacts.page
  "The contacts page: sticky page chrome — a header bar with the title, search
   and actions; a pinned pagination footer — around the shared collection,
   rendering every contact as the rolodex card ([[hai.contacts.card/card]])."
  (:require
   [com.wsscode.pathom3.connect.operation :as pco]
   [hai.contacts.card :as contact-card]
   [hai.contacts.create :as create]
   [hai.contacts.data :as data]
   [hai.ui.components :as c]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.form :as col.form]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.form.core :as form]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]))

(def ContactEditSchema
  [:map
   [:contact/uuid {:identity? true} :uuid]
   [:contact/full-name {:title "Name" :optional true} :string]
   [:contact/github-login {:title "GitHub" :optional true} :string]
   [:contact/linkedin-url {:title "LinkedIn" :optional true} :string]
   [:contact/type
    {:title "Type"
     :form.field.select/options [{:value :contact.type/individual   :title "Individual"}
                                 {:value :contact.type/organisation :title "Organisation"}]
     :optional true}
    [:enum :contact.type/individual :contact.type/organisation]]])

(defn- emails-render
  [{:contact/keys [email]}]
  [table/table-cell
   (table/label {:items (vec (or email []))})])

(defn- emails-domains-render
  [{:contact/keys [org-mailing-namespace]}]
  [table/table-cell
   (table/label {:items (vec (or org-mailing-namespace []))})])

(defn- source-account-render
  [{:contact/keys [auto-filled-from-connection-email]}]
  [table/table-cell
   (if auto-filled-from-connection-email
     [c/Tooltip {:content "Calendar account this contact was auto-added from"}
      [:span auto-filled-from-connection-email]]
     "—")])

(defn- members-render
  [{:contact/keys [members]}]
  [table/table-cell
   (table/label {:items (vec (or members []))
                 :xform (map (fn [m] (or (:contact/full-name m) "Unnamed")))
                 :text "—"})])

(defn- type-render
  [{:contact/keys [type]}]
  [table/table-cell
   (case (or (:db/ident type) type)
     :contact.type/organisation "Organisation"
     :contact.type/individual   "Individual"
     "—")])

(defn- contact-card-render
  [_collection {:form/keys [initial-value]}]
  [contact-card/card
   {:contact       initial-value
    :on-body-click (fn [] (>e [:routing/navigate [:contacts/detail initial-value]]))
    :on-delete     (fn [] (>e [::data/delete (:contact/uuid initial-value)]))}])

;; ---------------------------------------------------------------------------
;; Sticky page header — the notes wall's header shell ([[hai.notes.page]]) in
;; the contact tint, with the forest ink and sage lines the tasks board wears.
;; ---------------------------------------------------------------------------

(o/defstyled page-header :header
  {:position "sticky" :top "0" :z-index "50"
   :display "flex" :align-items "center" :flex-wrap "wrap"
   :gap "10px"
   :padding "10px 14px"
   :background t/--contact
   :border-bottom-width "2px" :border-bottom-style "dotted"
   :border-bottom-color t/--card-sage
   :border-radius "10px"}
  [:.header-icon
   {:display "inline-flex" :align-items "center"
    :color t/--card-forest}
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]]
  [:.header-title
   {:font-size "16px" :font-weight "700" :line-height "1.2"
    :margin "0" :color t/--card-forest}]
  [:input.header-search
   {:flex "0 1 340px" :min-width "160px"
    :padding "7px 12px"
    :border-width "1px" :border-style "solid" :border-color t/--card-sage-light
    :border-radius "8px"
    :background t/--colour-snow
    :color t/--card-forest
    :font-size "13px" :font-family "inherit"
    :outline "none"
    :transition "border-color 0.15s ease"}
   [:&::placeholder {:color t/--card-forest-70}]
   [:&:focus {:border-color t/--card-sage}]]
  [:.table-extras {:display "contents"}]
  [:.header-actions
   {:display "flex" :align-items "center" :gap "4px" :margin-left "auto"}]
  [:.header-btn
   {:width "28px" :height "28px"
    :border "none" :border-radius "999px"
    :background "transparent" :padding "0"
    :color t/--card-forest :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :transition "background 0.15s ease"}
   [#{:&:hover :&.active} {:background t/--card-forest-10}]
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]
   ;; Labeled variant (the add button): icon + text, sized to content. The
   ;; bare ⋮ settings button keeps the round icon-only base.
   [:&.labeled
    {:width "auto" :gap "6px" :padding "0 12px"
     :font-family "inherit" :font-size "13px" :font-weight "600"}]
   ;; Filled "Add contact" button — the deeper contact blue. Its own hover
   ;; keeps the fill (the shared header-btn hover would blank it back to
   ;; transparent).
   [:&.add
    {:background t/--contact-add-fill}
    [#{:&:hover :&.active} {:background t/--contact-add-fill :filter "brightness(0.95)"}]]])

;; The ⋮ popover's rows — the collection settings form's own look
;; (`settings/form-render`), minus its "Show filters" row: the page has no
;; filters.
(o/defstyled settings-rows :div
  :flex :flex-col :gap-2
  [:& :> [:div :flex :flex-row :items-center :gap-2]])

(defn- header-controls
  "`:form/render` for the collection's settings sub-form, mounted in the page
   header's actions cluster: the add-contact button and the ⋮ settings popover
   — Admin view, Case sensitive, and the View mode select, all the collection's
   own fields, so their persistence + reload wiring is untouched."
  [{fields :form/fields}]
  [:<>
   [c/Tooltip {:content "Add contact"}
    [:button.header-btn
     {:type       "button"
      :class      ["labeled" "add"]
      :aria-label "Add contact"
      :on-click   #(>e [::create/open {}])}
     [icons/plus {:size 16}]
     [:span "Add Contact"]]]
   [c/PopoverRoot
    [c/PopoverTrigger
     [:button.header-btn {:type "button" :aria-label "Contacts view settings"}
      [icons/filled-more_vert]]]
    [c/PopoverContent {:align "end"}
     [settings-rows
      (when @(<s :auth/admin?)
        [:label
         [:div
          [form/checkbox (get fields :admin?)]
          [c/Text "Admin view"]]])
      [:label
       [:div
        [form/checkbox (get fields :case-sensitive?)]
        [c/Text {:title "sorting, search, filtering"} "Case sensitive"]]]
      [form/render-field (get fields :collection/view)]]]]])

(defn- page-chrome
  "`:controls` for the contacts collection — the sticky page header: title,
   the always-visible search input (the collection's `:table/search` field),
   and the actions cluster. In table view the inline-edit Submit, the
   bulk-actions dropdown and the spreadsheet export stay available ahead of it."
  [table]
  (let [{:form.field/keys [current-value set-value!]}
        (get-in table [:table/form :form/fields :table/search])
        view           (-> table :table/form :form/current-value
                           :table/settings :collection/view)
        cards?         (not= :collection.view/table view)
        settings-field (get-in table [:table/form :form/fields :table/settings])]
    [page-header
     [:span.header-icon [icons/outlined-people]]
     [:h1.header-title "Contacts"]
     [:input.header-search
      {:type        "text"
       :value       (or current-value "")
       :placeholder "Search contacts…"
       :aria-label  "Search contacts"
       :on-change   (fn [e] (set-value! (.. e -target -value)))}]
     [:div.header-actions
      ;; An always-present display:contents slot keeps every header child at a
      ;; fixed position: a conditional sibling appearing would otherwise shift
      ;; the settings form/component and remount it, resetting the settings
      ;; sub-form to defaults.
      [:span.table-extras
       (when-not cards?
         [:<>
          [collection/submit-edits-button table]
          [bulk-actions/dropdown table]
          [bulk-actions/spreadsheet-export table]])]
      (when settings-field
        [form/component (assoc settings-field
                               :form/render header-controls
                               ;; A remount must never re-seed the sub-form —
                               ;; the live view state lives there.
                               :form/suppress-setup-if-done? true)])]]))

;; ---------------------------------------------------------------------------
;; Page layout — sticky chrome around the scrolling collection
;; ---------------------------------------------------------------------------

;; The collection renders its chrome and views as flat siblings; this wrapper
;; turns that flat list into a one-column page grid with the pagination footer
;; pinned to the viewport bottom.
(o/defstyled wall-layout :div
  [collection/component
   {:display "grid"
    :grid-template-columns "minmax(0, 1fr)"
    :gap "14px"}
   [cards/pagination-controls
    {:position "sticky" :bottom "0" :z-index "40"
     :background t/--hai-white-2
     :border-top-width "2px" :border-top-style "dotted"
     :border-top-color t/--card-sage}]])

(defn- contacts-table
  []
  [collection/component
   {:id data/table-id
    :update-mutation 'contact/update
    :schema ContactEditSchema
    :query [{:contacts/table
             [{:collection/items [:contact/uuid
                                  :contact/full-name
                                  :contact/github-login
                                  :contact/linkedin-url
                                  :contact/email
                                  :contact/org-mailing-namespace
                                  (pco/? :contact/auto-filled-from-connection-email)
                                  (pco/? {:contact/type [:db/ident]})
                                  (pco/? {:contact/humble-user [:user/uuid (pco/? :user/full-name)]})
                                  (pco/? {:contact/members [:contact/uuid (pco/? :contact/full-name)]})]}
              :total :cursor :prev-cursor :next-cursor]}]
    :paginate? true
    :pagination-position :bottom
    :pagination {:direction :desc :sort-attrs [:contact/updated-at]}
    :default-view :collection.view/cards
    :controls page-chrome
    :no-items (fn [_] [:em "No contacts yet — add your first contact."])
    :card contact-card-render
    :columns [{:header "Name"
               :attr :contact/full-name
               :field (col.form/link-input :contacts/detail)}
              {:header "Type"
               :attr :contact/type
               :field col.form/single-select
               :render type-render
               :sort? false}
              {:header "Emails"
               :attr :contact/email
               :sort? false
               :render emails-render}
              {:header "Email Domains"
               :attr :contact/org-mailing-namespace
               :sort? false
               :render emails-domains-render}
              {:header "Source account"
               :attr :contact/auto-filled-from-connection-email
               :sort? false
               :render source-account-render}
              {:header "GitHub"
               :attr :contact/github-login
               :field col.form/input}
              {:header "LinkedIn"
               :attr :contact/linkedin-url
               :field col.form/input
               :sort? false}
              {:header "Members"
               :attr :contact/members
               :sort? false
               :render members-render}]
    :bulk-actions [{:title "Delete"
                    :mutation 'contact/delete
                    :alert {:content (fn [{:table/keys [selected] :as table*} action]
                                       [bulk-actions/confirm-dialog
                                        {:table  table*
                                         :action action}
                                        [bulk-actions/title "Delete contacts"]
                                        [bulk-actions/description "Are you sure you want to delete the following contacts?"]
                                        (into [:ul]
                                              (map (fn [{:contact/keys [full-name email]}]
                                                     [:li [:strong (or full-name (first email) "Unnamed")]]))
                                              selected)])}}]}])

(defn page
  [_route]
  [layout/page
   [wall-layout [contacts-table]]])
